import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import Modal from '@mui/material/Modal';
import { Box, Tabs, Tab } from '@mui/material';
import CheckSheet from 'src/screen/Protected/DeviceScreen/TestingReport/CheckSheet';
import CommissioningSignOff from 'src/screen/Protected/DeviceScreen/Commissioning/CommissioningSignOff';
import Button from 'src/component/UI/Button';
import CardButton from 'src/component/UI/CardButton';
import { getDeviceById } from 'src/module/device/selector';
import { fetchDevice } from 'src/module/device/action';
import { Map, fromJS, List } from 'immutable';
import { getAbilities } from 'src/module/authentication/selector';
import { fetchCheckSheet, fetchCheckSheetTasks, saveCheckSheet, updateCheckSheet } from 'src/module/checkSheet/action';
import { getCheckSheetReport, getCheckSheetTasks, getCheckSheetTasksLoading, getSaveCheckSheetReportLoading } from 'src/module/checkSheet/selector';
import LogoSpinner from 'src/component/UI/LogoSpinner';
import useMediaQuery from '@mui/material/useMediaQuery';
import variables from 'src/style/variable/variables.module.scss';
import { useTranslation } from 'src/i18n';
import CircuitDynamicTable from '../CommissioningCircuit/CircuitDynamicTable';
import CableInsulationInfo from './CableInsulationInfo';
import { resolveEffectiveDeviceId } from '../Commissioning/utils';
import { useCommissioningDeviceLink } from '../Commissioning/useCommissioningDeviceLink';
import CableInsulationMeasurements from './CableInsulationMeasurements';

// Shared implementation for the pre- and post-cable-insulation commissioning reports. The two
// forms are identical apart from the values below; everything variant-specific lives in this one
// table so the component body has no branching and the wrappers stay trivial.
const VARIANT_CONFIG = {
  pre: {
    reportType: 'commissioning-cable-pre-insulation',
    generalType: 'commissioning-cable-pre-insulation-general',
    i18nPrefix: 'commissioningCablePreInsulation',
    checksKey: 'commissioningCablePreInsulation.preInsulationChecks',
  },
  post: {
    reportType: 'commissioning-cable-post-insulation',
    generalType: 'commissioning-cable-post-insulation-general',
    i18nPrefix: 'commissioningCablePostInsulation',
    checksKey: 'commissioningCablePostInsulation.postInsulationChecks',
  },
};

export default function CableInsulationReport (props) {
  const { variant, deviceId, reportId, open } = props;
  const { reportType, generalType, i18nPrefix, checksKey } = VARIANT_CONFIG[variant];

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [tabValue, setTabValue] = useState('1');
  const selectGeneralTasks = useMemo(() => getCheckSheetTasks(generalType), [generalType]);
  const generalTasks = useSelector(selectGeneralTasks);
  const report = useSelector(getCheckSheetReport(reportId));
  const device = useSelector(getDeviceById(deviceId));
  const isSaveLoading = useSelector(getSaveCheckSheetReportLoading(reportType));

  const isLoadingTasks = useSelector(getCheckSheetTasksLoading);
  const isMobile = useMediaQuery(`(max-width: ${variables.mobileWidth})`);
  const abilities: any = useSelector(getAbilities);
  const isPhoneView = useMediaQuery(`(max-width: ${variables.phoneWidth})`);

  const canEdit = abilities.includes('edit-check-sheet-report');
  const readOnly = Boolean(reportId) && !canEdit;

  const buildEmptyResults = (tasks) => {
    const tasksList = tasks.toJS ? tasks.toJS() : [];
    return fromJS({
      info: {
        type: reportType,
        deviceId: deviceId || null,
      },
      [generalType]: tasksList.map((task: any) => ({
        task_id: task.key,
        status: null,
        comment: '',
      })),
      extras: {
        info: {
          heatTraceCircuit: device?.get('tag') || '',
          heatTraceCableTag: '',
          // Prefilled from the linked device.
          unit: device?.get('unit') || '',
          heaterType: device?.get('heater_type') || '',
          system: device?.get('system') || '',
          location: device?.get('location') || '',
          lineNo: device?.get('line') || '',
          isoNumber: device?.get('iso_number') || '',
        },
        measurements: {
          cableResistance: '', srCableLength: '', srCapacitance: '',
          meggerInsulationResistance1: '', meggerTestVoltage1: '',
          meggerInsulationResistance2: '', meggerTestVoltage2: '',
          meggerInsulationResistance3: '', meggerTestVoltage3: '',
          subCircuits: [],
        },
        testInstruments: [],
        deficiencies: [],
      },
      signOff: {
        recommendations: '',
        commissioningStatus: '',
      },
    }) as Map<string, any>;
  };

  const [allResults, setAllResults] = useState(Map({}));
  const [submitAttempted, setSubmitAttempted] = useState(false);

  useEffect(() => {
    if (!reportId && generalTasks && generalTasks.size > 0) {
      setAllResults(buildEmptyResults(generalTasks));
    }
  }, [generalTasks, reportId]);

  useEffect(() => {
    if (open && !reportId) {
      setTabValue('1');
      setSubmitAttempted(false);
      if (generalTasks && generalTasks.size > 0) {
        setAllResults(buildEmptyResults(generalTasks));
      }
    }
  }, [open]);

  useEffect(() => {
    dispatch(fetchCheckSheetTasks(generalType));
  }, []);

  // Opened from the commissioning screen the device may never have been visited, so its full
  // record (unit/heater_type/system/location/line) isn't in the store. Fetch it so the
  // fill-empty effect below can prefill the info fields.
  useEffect(() => {
    if (open && deviceId) {
      dispatch(fetchDevice(deviceId));
    }
  }, [open, deviceId, dispatch]);

  useEffect(() => {
    if (open && reportId) {
      dispatch(fetchCheckSheet(reportId, generalType));
    }
  }, [open, reportId, dispatch]);

  useEffect(() => {
    if (reportId && report.get('checkSheet')) {
      setAllResults(Map(report));
    }
  }, [report, reportId]);

  // The device currently linked in the form (may differ from the prop device after a relink).
  const linkedDeviceId = allResults.getIn(['info', 'deviceId']) as string | null;
  const linkedDevice = useSelector(getDeviceById(linkedDeviceId || ''));

  // When a device is (re)linked, fill any info fields the device knows about that are still empty.
  // Only empties are touched so we never clobber values the user already entered.
  useEffect(() => {
    if (!linkedDevice || linkedDevice.isEmpty()) return;
    setAllResults((prev) => {
      if (!prev.get('extras')) return prev;
      let next = prev;
      const fillIfEmpty = (field: string, deviceKey: string) => {
        if (!prev.getIn(['extras', 'info', field])) {
          const val = linkedDevice.get(deviceKey);
          if (val) next = next.setIn(['extras', 'info', field], val);
        }
      };
      fillIfEmpty('heatTraceCircuit', 'tag');
      fillIfEmpty('unit', 'unit');
      fillIfEmpty('heaterType', 'heater_type');
      fillIfEmpty('system', 'system');
      fillIfEmpty('location', 'location');
      fillIfEmpty('lineNo', 'line');
      fillIfEmpty('isoNumber', 'iso_number');
      return next;
    });
  }, [linkedDevice]);

  const testInstrumentColumns = useMemo(() => [
    { key: 'manufacturer', label: t('commissioningCircuit.testInstruments.manufacturer') },
    { key: 'model', label: t('commissioningCircuit.testInstruments.model') },
    { key: 'range', label: t('commissioningCircuit.testInstruments.range') },
    { key: 'idNo', label: t('commissioningCircuit.testInstruments.idNo') },
    { key: 'calibrationDueDate', label: t('commissioningCircuit.testInstruments.calibrationDueDate'), type: 'date' },
  ], [t]);

  const deficienciesColumns = useMemo(() => [
    { key: 'item', label: t('commissioningCircuit.deficiencies.item') },
    { key: 'type', label: t('commissioningCircuit.deficiencies.type') },
    { key: 'notes', label: t('commissioningCircuit.deficiencies.notes') },
  ], [t]);

  const { handleUnlinkDevice, handleDeviceSelect } = useCommissioningDeviceLink(
    setAllResults,
    // Load the linked device so the fill-empty effect can populate system/location/lineNo.
    useCallback((id: string) => dispatch(fetchDevice(id)), [dispatch]),
  );
  const subCircuits = (allResults.getIn(['extras', 'measurements', 'subCircuits']) as List<any>) || List();

  // Which sub-circuit block should take focus when it mounts
  const focusSubCircuitIdx = useRef<number | null>(null);

  useEffect(() => {
    focusSubCircuitIdx.current = null;
  }, [subCircuits.size]);

  const addSubCircuitMeasurement = () => {
    focusSubCircuitIdx.current = subCircuits.size;
    setAllResults((prev) => prev?.updateIn(
      ['extras', 'measurements', 'subCircuits'],
      (existing) => ((existing as List<any>) || List())?.push(Map()),
    ));
  };

  // Blocks are keyed by index, so removing one shifts every later block up a slot; each
  // remaining instance re-reads its values from the new `data` on the same path.
  const deleteSubCircuitMeasurement = (idx: number) => {
    setAllResults((prev) => prev?.updateIn(
      ['extras', 'measurements', 'subCircuits'],
      (existing) => ((existing as List<any>) || List())?.delete(idx),
    ));
  };

  if (!open) {
    return null;
  }

  if (reportId && !report.get('checkSheet')) {
    return null;
  }

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    setTabValue(newValue);
  };

  const statusError = submitAttempted && !allResults.getIn(['signOff', 'commissioningStatus']);

  const next = () => setTabValue((parseInt(tabValue) + 1).toString());
  const previous = () => setTabValue((parseInt(tabValue) - 1).toString());

  const submit = () => {
    if (!allResults.getIn(['signOff', 'commissioningStatus'])) {
      setSubmitAttempted(true);
      setTabValue('4');
      return;
    }
    const values: any = allResults.toJS();
    const c = [...(values[generalType] || [])];
    const effectiveDeviceId = resolveEffectiveDeviceId(allResults, deviceId);
    let payload = allResults
      .setIn(['info', 'type'], reportType)
      .setIn(['info', 'deviceId'], effectiveDeviceId)
      .set('checkSheet', fromJS(c))
      .set('signOff', allResults.get('signOff'));
    if (reportId) {
      payload = payload.set('id', reportId);
      dispatch(updateCheckSheet(payload, effectiveDeviceId, reportType));
    } else {
      dispatch(saveCheckSheet(reportType, payload, effectiveDeviceId));
    }
  };

  const change = (path, value) => {
    setAllResults(prev => prev.setIn(path, value));
  };

  const circuitTag = device ? device.get('tag') : null;
  const parentTag = device ? device.get('parent_tag') : null;
  const deviceTitleTag = parentTag ? `${parentTag}-${circuitTag}` : circuitTag;
  const titleTag = deviceTitleTag || allResults?.getIn(['extras', 'info', 'heatTraceCircuit']);

  return (
    <Modal open={props.open} onClose={props.handleClose} style={{ height: '90vh' }}>
      <div className={`modal testing-report-modal commissioning-modal${isPhoneView ? ' modal-no-padding-sides' : ''}`}>
        {!isMobile && (
          <h2>{t(`${i18nPrefix}.modalTitle`)} {titleTag && <b>({titleTag})</b>}</h2>
        )}
        {isLoadingTasks ? (
          <div style={{ margin: 'auto', marginTop: '200px', width: '100px' }}>
            <LogoSpinner size={100} />
          </div>
        ) : (
          <>
            <Box sx={{ width: '100%', marginBottom: '20px' }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                textColor='primary'
                indicatorColor='primary'
                aria-label={`${reportType}-tabs`}
                variant="scrollable"
                scrollButtons="auto"
              >
                <Tab value="1" label={<span className='tab-label-size'>{t('commissioningCircuit.info')}</span>} />
                <Tab value="2" label={<span className='tab-label-size'>{t(checksKey)}</span>} />
                <Tab value="3" label={<span className='tab-label-size'>{t('commissioningCircuit.testInstrumentsAndDeficiencies')}</span>} />
                <Tab value="4" label={<span className='tab-label-size'>{t('commissioningCircuit.signOffAndComments')}</span>} />
              </Tabs>
            </Box>

            <div className='commissioning-modal-content'>
              {tabValue === '1' && (
                <>
                  <CableInsulationInfo
                    i18nPrefix={i18nPrefix}
                    readOnly={readOnly}
                    data={allResults?.get('extras')}
                    onChange={change}
                    linkedDeviceTag={device?.get('tag')}
                    onUnlink={handleUnlinkDevice}
                    onDeviceSelect={handleDeviceSelect}
                  />
                  <CableInsulationMeasurements
                    key='cable-measurement-main'
                    i18nPrefix={i18nPrefix}
                    readOnly={readOnly}
                    data={allResults?.get('extras')}
                    onChange={change}
                  />
                  {subCircuits.map((_subCircuit, idx) => (
                    <CableInsulationMeasurements
                      key={`cable-measurement-sub-${idx}`}
                      i18nPrefix={i18nPrefix}
                      readOnly={readOnly}
                      data={allResults?.get('extras')}
                      onChange={change}
                      isSubCircuit
                      subCircuitIdx={idx}
                      autoFocusTag={focusSubCircuitIdx.current === idx}
                      onDelete={() => deleteSubCircuitMeasurement(idx)}
                    />
                  ))}
                  <CardButton
                    className='add-sub-circuit-slot'
                    onClick={addSubCircuitMeasurement}
                    disabled={readOnly}
                  >
                    {t('commissioning.cableInsulation.addSubCircuit')}
                  </CardButton>
                </>
              )}
              {tabValue === '2' && (
                <CheckSheet
                  readOnly={readOnly}
                  onChange={change}
                  id={generalType}
                  title={t(checksKey)}
                  data={allResults?.get(generalType)}
                  checkSheetType={generalType}
                />
              )}
              {tabValue === '3' && (
                <>
                  <CircuitDynamicTable
                    title={t('commissioningCircuit.testInstruments.title')}
                    columns={testInstrumentColumns}
                    data={allResults?.getIn(['extras', 'testInstruments']) as List<any>}
                    extrasKey="testInstruments"
                    onChange={change}
                    readOnly={readOnly}
                  />
                  <CircuitDynamicTable
                    title={t('commissioningCircuit.deficiencies.title')}
                    columns={deficienciesColumns}
                    data={allResults?.getIn(['extras', 'deficiencies']) as List<any>}
                    extrasKey="deficiencies"
                    onChange={change}
                    readOnly={readOnly}
                    typeLegend={t('commissioningCircuit.deficiencies.typeLegend')}
                  />
                </>
              )}
              {tabValue === '4' && (
                <CommissioningSignOff
                  readOnly={readOnly}
                  data={allResults?.get('signOff')}
                  onChange={change}
                  statusError={statusError}
                />
              )}
            </div>

            <div className='button-bar stay-at-bottom'>
              <Button onClick={() => props.handleClose()}>{t('common.cancel')}</Button>
              {tabValue !== '1' && <Button onClick={previous}>{t('common.previous')}</Button>}
              {tabValue === '4'
                ? <Button onClick={submit} disabled={readOnly} loading={isSaveLoading} cta>{reportId ? t('common.save') : t('common.submit')}</Button>
                : <Button className='next-button' onClick={next} cta>{t('common.next')}</Button>
              }
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

CableInsulationReport.propTypes = {
  variant: PropTypes.oneOf(['pre', 'post']).isRequired,
  handleClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  deviceId: PropTypes.string,
  reportId: PropTypes.string,
};
